import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@v2/lib/api';

type Paginated<T> = {
    data: T[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from?: number | null;
    to?: number | null;
};

export function useResourceList<T>(resource: string, params?: Record<string, string | number | boolean>) {
    return useQuery({
        queryKey: ['resource', resource, params],
        queryFn: async () => {
            const { data } = await api.get<Paginated<T>>(`/${resource}`, { params });
            return data;
        },
    });
}

export function useResourceShow<T>(resource: string, id: string | undefined) {
    return useQuery({
        queryKey: ['resource', resource, id],
        enabled: Boolean(id),
        queryFn: async () => {
            const { data } = await api.get<{ data: T }>(`/${resource}/${id}`);
            return data.data;
        },
    });
}

export function useResourceMutation(resource: string) {
    const qc = useQueryClient();

    const invalidate = () => qc.invalidateQueries({ queryKey: ['resource', resource] });

    const create = useMutation({
        mutationFn: async (payload: Record<string, unknown>) => {
            const { data } = await api.post(`/${resource}`, payload);
            return data;
        },
        onSuccess: invalidate,
    });

    const update = useMutation({
        mutationFn: async ({ id, payload }: { id: string | number; payload: Record<string, unknown> }) => {
            const { data } = await api.patch(`/${resource}/${id}`, payload);
            return data;
        },
        onSuccess: invalidate,
    });

    const remove = useMutation({
        mutationFn: async (id: string | number) => {
            await api.delete(`/${resource}/${id}`);
        },
        onSuccess: invalidate,
    });

    return { create, update, remove };
}

export function useNavigationMeta() {
    return useQuery({
        queryKey: ['meta', 'navigation', window.__V2__?.locale ?? 'pt_BR'],
        queryFn: async () => {
            const { data } = await api.get<{ navigation: Array<{ group: string; label: string; items: Array<{ name: string; href: string; badge?: number; badge_tone?: 'warning' }> }> }>(
                '/meta/resources',
            );
            return data.navigation;
        },
        staleTime: 30_000,
        refetchInterval: 60_000,
    });
}
